<script>
  import { faArrowsTurnToDots } from "@fortawesome/pro-solid-svg-icons"
  import { Button, Icon, Text, Select, SelectItem, Flex, FlexItem } from "@gl/design-system"
  import { _ } from "../../stores/index"
  import { RolesRepository, PoliciesRepository } from "../../repositories/index"
  import { onMount } from "svelte"

  export let role

  const COMPARING_COLORS = ["#002735", "#003d54", "#00678d"]

  let loading = {}
  let roles = []
  let policies = []

  let policiesObjs = []
  let searched

  let existingApps = []
  let applicationView = "all"

  let comparingRoles = [{ name: role.data.name, id: role.id }, { name: "-" }, { name: "-" }]

  let rolesSelect = []

  async function fetchRoles() {
    try {
      loading.roles = true
      const result = await RolesRepository.getRoles()
      roles = result.list.filter((r) => r.id !== role.id)
    } finally {
      loading.roles = false
    }
  }

  async function fetchContractPolicies() {
    try {
      const result = await PoliciesRepository.getPoliciesOfContract()
      policies = result.list
    } finally {
    }
  }

  async function fetchRolePolicies(roleID) {
    try {
      loading.roles = true
      const result = await RolesRepository.getRolePolicies(roleID)
      let rolePolicies = result.list

      let mappedRolePolicies = policies.filter((p) => rolePolicies.some((rp) => rp === p.id))

      // add implicit policies ass explicit to display on table
      for (let rp of mappedRolePolicies) {
        if (rp.implicit) {
          const implicitPolicies = rp.implicit.split(",")
          for (let implicitPolicy of implicitPolicies) {
            const implicitPolicyObj = policies.find((p) => p.name === implicitPolicy && rp.appId === p.appId)
            if (!mappedRolePolicies.some((el) => el.id === implicitPolicyObj.id))
              mappedRolePolicies.push({ ...implicitPolicyObj, implicitPolicy: true })
          }
        }
      }

      return mappedRolePolicies
    } finally {
      loading.roles = false
    }
  }

  async function mapRolePolicies(roleID) {
    loading.policies = true

    try {
      // get policies of selected role and add it to comparingRoles object
      const rolePolicies = await fetchRolePolicies(roleID)
      comparingRoles = comparingRoles.map((r) => {
        return r.id === roleID ? { ...r, policies: rolePolicies } : r
      })

      // show on table objs coming from current roles
      for (const policy of rolePolicies) {
        if (!policiesObjs.some((pO) => pO.obj === policy.obj && pO.appId === policy.appId)) {
          policiesObjs.push({
            obj: policy.obj,
            appName: policy.appName,
            appId: policy.appId,
            appType: policy.appType,
            roleID: roleID,
          })
        }
      }

      // show apps filter
      existingApps = rolePolicies.reduce((acc, curr) => {
        if (!acc.some((a) => a.id === curr.appId)) {
          acc.push({ id: curr.appId, name: curr.appName, type: curr.appType })
        }
        return acc
      }, [])
    } finally {
      loading.policies = false
    }
  }

  async function fetchAll() {
    loading.roles = true
    loading.policies = true
    try {
      await Promise.all([fetchRoles()]).then(() => {
        fetchContractPolicies().then(() => {
          mapRolePolicies(role.id)
        })
      })
    } finally {
      loading.roles = false
      loading.policies = false
    }
  }

  function handleCompare() {
    // clean policies except main role policies
    if (policiesObjs.length) policiesObjs = policiesObjs.filter((o) => o.roleID === role.id)

    for (let i = 0; i < rolesSelect.length; i++) {
      if (rolesSelect[i]) {
        comparingRoles[i + 1] = rolesSelect[i]
        mapRolePolicies(rolesSelect[i].id)
      }
    }
  }

  function arePoliciesDifferentThanMain(obj, comparingRole) {
    if (comparingRole.name === "-" || comparingRole.id === role.id || !comparingRole.policies) return null

    const mainRole = comparingRoles.find((r) => r.id === role.id)
    if (!mainRole) return null

    const policiesThatMainDoesNotHave = comparingRole.policies.filter(
      (policy1) => !mainRole.policies.some((policy2) => policy2.id === policy1.id)
    )
    const policiesThatComparingRoleDoesNotHave = mainRole.policies.filter(
      (policy1) => !comparingRole.policies.some((policy2) => policy2.id === policy1.id)
    )

    const differentPolicies = [...policiesThatComparingRoleDoesNotHave, ...policiesThatMainDoesNotHave]
    const policiesWithCurrentObj = differentPolicies.filter((p) => p.obj === obj.obj && p.appId === obj.appId)

    return policiesWithCurrentObj.length > 0
  }

  const handleApplicationsViewChange = () => {
    if (applicationView === "all") {
      searched = null
    } else {
      searched = policiesObjs.filter((o) => o.appId === applicationView)
    }
  }

  onMount(() => {
    fetchAll()
  })
</script>

{#if !loading.roles}
  <Flex class="align-items-center">
    <FlexItem>
      <Select bind:value={applicationView} on:change={() => handleApplicationsViewChange()}>
        <SelectItem label={`${$_("common.apps")}: All`} value="all" />
        {#each existingApps as app}
          <SelectItem label={`${$_("common.apps")}: ${app.name} (${app.type})`} value={app.id} />
        {/each}
      </Select>
    </FlexItem>

    <div class="vertical-line mx-3" />

    {#each comparingRoles.filter((cr) => cr.id !== role.id) as comparingRole, index}
      <FlexItem class="mr-3">
        <Select bind:value={rolesSelect[index]}>
          {#each roles as role}
            <SelectItem label={role.name} value={role} />
          {/each}
        </Select>
      </FlexItem>
    {/each}

    <FlexItem>
      <Button size="small mt-1" on:click={handleCompare}>
        <Icon icon={faArrowsTurnToDots} />
        <Text>{$_("common.compare")}</Text>
      </Button>
    </FlexItem>
  </Flex>
{/if}
{#if !loading.policies}
  <table class="my-5">
    <thead>
      <tr>
        <th style="--background-color: var(--gl-background); ">{$_("common.app.type")}</th>
        <th style="--background-color: var(--gl-background); --border-left-color: var(--gl-text-light)"
          >{$_("common.policy")}</th
        >
        {#each comparingRoles as comparingRole, index}
          <th
            style={`--color: white; --background-color:${COMPARING_COLORS[index]}; --border-right-color:${COMPARING_COLORS[index]}; `}
          >
            {comparingRole.name}
          </th>
        {/each}
      </tr>
      {#each searched || policiesObjs as obj}
        <tr>
          <td style="--border-left-color: var(--gl-background)">
            {obj.appName}
            <p class="text-light">({obj.appType})</p>
          </td>
          <td style="--border-left-color: var(--gl-background)">{obj.obj}</td>

          {#each comparingRoles as comparingRole, index}
            <td
              style={`--border-right-color:${COMPARING_COLORS[index]}; ${
                index === 0 ? `--border-left-color:${COMPARING_COLORS[index]}` : ""
              }`}
              class:background-color-info-light={arePoliciesDifferentThanMain(obj, comparingRole)}
            >
              <div class="policies-container">
                {#each comparingRole.policies?.filter((p) => p.obj === obj.obj && p.appId === obj.appId) || [] as policy}
                  <div class="mr-2 p-1 policy" class:implicit={policy.implicitPolicy}>
                    {policy.act},
                  </div>
                {/each}
              </div>
            </td>
          {/each}
        </tr>
      {/each}
    </thead>
  </table>
{/if}

<style>
  table {
    width: 100%;
  }

  th {
    background-color: var(--background-color);
    color: var(--color);
    font-weight: bold;
    padding: 10px;
    border-left: 1px solid var(--border-left-color);
    border-right: 1px solid var(--border-right-color);
    position: sticky;
    top: 0;
    z-index: 1;
    font-size: 14px;
    width: 20%;
  }

  td {
    border-left: 1px solid var(--border-left-color);
    border-right: 1px solid var(--border-right-color);
    border-top: 1px solid rgb(217, 217, 217);
    border-bottom: 1px solid rgb(217, 217, 217);
    padding: 10px;
    background-color: var(--gl-background);
    width: 20%;
    max-width: 200px;
  }

  .policies-container {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 0px;
  }

  .policy {
    width: fit-content;
  }

  .implicit {
    color: #bfbfbf;
  }
</style>
